import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useLocalization } from "@/hooks/useLocalization";
import { normalizeLang, type Lang } from "@/i18n/languages";
import { getArticle } from "@/data/articles";

const MAIN_STORE = "https://kaspreadsheet.com";

export default function Article() {
  const params = useParams();
  const lang = normalizeLang(params.lang);
  const { t } = useTranslation();
  const article = getArticle(lang as Lang, params.slug || "");

  // Scroll to top on page load / slug change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [params.slug]);

  if (!article) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <Navbar />
        <main className="flex-1">
          <div className="mx-auto w-full max-w-4xl px-4 py-20 text-center md:px-6">
            <h1 className="font-heading text-2xl font-bold text-foreground-950">
              {t("articleNotFound.title")}
            </h1>
            <p className="mt-3 text-sm text-foreground-600">
              {t("articleNotFound.text")}
            </p>
            <Link
              to={`/${lang}`}
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              {t("nav.home")}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <Navbar />

      <main className="flex-1">
        {/* Article header */}
        <section className="w-full bg-background-100/70">
          <div className="mx-auto w-full max-w-4xl px-4 py-12 md:px-6 md:py-16">
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-foreground-500">
              <Link to={`/${lang}`} className="hover:text-primary-700 transition-colors cursor-pointer">
                {t("breadcrumb.home")}
              </Link>
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-foreground-700 whitespace-nowrap">Guide</span>
            </nav>

            <h1 className="mt-4 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {article.h1}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </span>
                {article.readMinutes} {t("articles.minRead")}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line"></i>
                </span>
                {t("articles.updated")}: {article.updated}
              </span>
            </div>
          </div>
        </section>

        <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-12">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[280px_1fr]">
            {/* TOC sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 rounded-lg border border-background-300/60 bg-background-50 p-5">
                <h4 className="text-sm font-semibold text-foreground-950 mb-4">
                  {t("articles.title")}
                </h4>
                <ul className="space-y-2">
                  {article.toc.map((heading, i) => (
                    <li key={i}>
                      <a
                        href={`#section-${i}`}
                        className="text-sm text-foreground-600 hover:text-primary-700 transition-colors cursor-pointer"
                      >
                        {heading}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Article body */}
            <article className="min-w-0">
              <img
                src={article.image}
                alt={article.title}
                className="mb-8 h-56 w-full rounded-lg object-cover object-top md:h-80"
              />
              <p className="text-base leading-relaxed text-foreground-800 md:text-lg">
                {article.intro}
              </p>

              {article.sections.map((section, i) => (
                <section key={i} id={`section-${i}`} className="mt-8">
                  <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((p, j) => (
                    <p key={j} className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                      {p}
                    </p>
                  ))}
                </section>
              ))}

              {/* Comparison table */}
              <section id={`section-${article.toc.length - 2}`} className="mt-10">
                <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                  {article.comparisonTitle}
                </h2>
                <div className="mt-4 overflow-hidden rounded-lg border border-background-300/60">
                  <table className="w-full text-left text-sm">
                    <tbody>
                      {article.comparison.map((row, i) => (
                        <tr
                          key={i}
                          className={`border-b border-background-200/50 last:border-0 ${
                            i % 2 === 0 ? "bg-background-50" : "bg-background-100/60"
                          }`}
                        >
                          <th className="w-1/3 px-4 py-3 font-medium text-foreground-600 whitespace-nowrap">
                            {row.feature}
                          </th>
                          <td className="px-4 py-3 text-foreground-800">{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* FAQ */}
              <section id={`section-${article.toc.length - 1}`} className="mt-10">
                <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                  {t("faq.title")}
                </h2>
                <div className="mt-4 space-y-3">
                  {article.faq.map((item, i) => (
                    <div
                      key={i}
                      className="rounded-lg border border-background-300/60 bg-background-50 p-5"
                    >
                      <h3 className="text-sm font-semibold text-foreground-900 md:text-base">
                        {item.q}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.a}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Internal links */}
              <div className="mt-10 rounded-lg border border-background-300/60 bg-secondary-50/60 p-6">
                <h3 className="text-sm font-semibold text-foreground-950">
                  {article.relatedLabel}
                </h3>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Link
                    to={`/${lang}`}
                    className="inline-flex items-center gap-1.5 rounded-md bg-background-50 px-4 py-2 text-sm font-medium text-primary-700 border border-background-300/60 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-home-4-line"></i>
                    </span>
                    {t("nav.home")}
                  </Link>
                  <a
                    href={MAIN_STORE}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-1.5 rounded-md bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 border border-background-300/60 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-shopping-bag-3-line"></i>
                    </span>
                    {t("hero.cta.buy")}
                  </a>
                </div>
              </div>

              {/* Final CTA */}
              <div className="mt-10 rounded-lg bg-primary-500 p-8 text-center">
                <h3 className="font-heading text-xl font-semibold text-background-50 md:text-2xl">
                  {t("hero.title")}
                </h3>
                <p className="mt-2 text-sm text-background-100/90">{t("hero.copy")}</p>
                <a
                  href={MAIN_STORE}
                  target="_blank"
                  rel="noopener nofollow"
                  className="mt-5 inline-flex items-center gap-2 rounded-md bg-background-50 px-7 py-3 text-sm font-semibold text-primary-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t("hero.cta.primary")}
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-sm"></i>
                  </span>
                </a>
              </div>
            </article>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}